import { TemplateDirectionEnum } from '__generated__/types';
import { FormInstance } from 'antd';
import { TemplateModalFormFieldEnum, TemplateModalFormValues } from 'components/TemplatesModal/TemplateModal.types';
import { Dispatch, MutableRefObject, SetStateAction, useEffect } from 'react';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';
import { TemplateModalQuery } from 'utils/graphql/queries/__generated__/templateModal';

interface Args {
  form: FormInstance<TemplateModalFormValues>;
  templateModalData: TemplateModalQuery;
  category: TemplateModalQuery['messengerTemplateCategories'][number] | undefined;
  selectedTemplateGroup: TemplateModalQuery['messengerTemplateCategories'][number]['templatesGroups'][number] | null;
  bulkCategoryId: string | null;
  isBulkActions: boolean;
  isVisibleToClient: boolean;
  isVisibleToPartner: boolean;
  isInitialisedAfterCategoryChange: MutableRefObject<boolean>;
  setSelectedCategoryId: Dispatch<SetStateAction<string | null>>;
  setTemplateGroupId: Dispatch<SetStateAction<string | null>>;
  handleCategoryChange: (categoryId: string) => void;
  handleTemplateConstantNameChange: (constantName: string) => void;
  handleTemplateSelect: (template: TemplateFragment | undefined) => void;
}

export const useTemplatesModalFormInitialization = ({
  form,
  templateModalData,
  category,
  selectedTemplateGroup,
  bulkCategoryId,
  isBulkActions,
  isVisibleToClient,
  isVisibleToPartner,
  isInitialisedAfterCategoryChange,
  setSelectedCategoryId,
  setTemplateGroupId,
  handleCategoryChange,
  handleTemplateConstantNameChange,
  handleTemplateSelect,
}: Args) => {
  useEffect(() => {
    isInitialisedAfterCategoryChange.current = false;
  }, [category?.id, isInitialisedAfterCategoryChange]);

  useEffect(() => {
    setTemplateGroupId(selectedTemplateGroup?.id ?? null);
  }, [selectedTemplateGroup?.id, setTemplateGroupId]);

  useEffect(() => {
    if (!templateModalData) {
      return;
    }

    const categories = templateModalData.messengerTemplateCategories ?? [];
    const preferredCategoryId = isBulkActions ? bulkCategoryId : (categories[0]?.id ?? null);
    const categoryId = preferredCategoryId ?? categories[0]?.id ?? null;

    if (!categoryId) {
      setSelectedCategoryId(null);
      setTemplateGroupId(null);
      form.setFieldValue(TemplateModalFormFieldEnum.Category, undefined);
      form.setFieldValue(TemplateModalFormFieldEnum.TemplateConstantName, undefined);
      form.setFieldValue(TemplateModalFormFieldEnum.Locale, undefined);
      handleTemplateSelect(undefined);

      return;
    }

    form.setFieldValue(TemplateModalFormFieldEnum.Category, categoryId);
    handleCategoryChange(categoryId);
  }, [
    bulkCategoryId,
    form,
    handleCategoryChange,
    handleTemplateSelect,
    isBulkActions,
    setSelectedCategoryId,
    setTemplateGroupId,
    templateModalData,
  ]);

  useEffect(() => {
    if (isInitialisedAfterCategoryChange.current || !category) {
      return;
    }

    isInitialisedAfterCategoryChange.current = true;

    let initialDirection: TemplateDirectionEnum | null = null;

    if (isVisibleToClient) {
      initialDirection = TemplateDirectionEnum.X2c;
    } else if (isVisibleToPartner) {
      initialDirection = TemplateDirectionEnum.X2p;
    }

    let initialConstantName = category.templatesGroups[0]?.constantName;

    if (initialDirection) {
      const templateGroupByInitialDirection = category.templatesGroups.find(
        ({ direction }) => direction === initialDirection,
      );

      if (templateGroupByInitialDirection) {
        initialConstantName = templateGroupByInitialDirection.constantName;
      } else {
        initialConstantName = category.templatesGroups.find(
          ({ direction }) => direction === TemplateDirectionEnum.Any,
        )?.constantName;
      }
    }

    if (initialConstantName) {
      handleTemplateConstantNameChange(initialConstantName);

      return;
    }

    form.setFieldValue(TemplateModalFormFieldEnum.TemplateConstantName, undefined);
    form.setFieldValue(TemplateModalFormFieldEnum.Locale, undefined);
    handleTemplateSelect(undefined);
  }, [
    category,
    form,
    handleTemplateConstantNameChange,
    handleTemplateSelect,
    isInitialisedAfterCategoryChange,
    isVisibleToClient,
    isVisibleToPartner,
  ]);
};
